import type { TooltipContext } from '../types/chart.types'

const tooltipStyles = {
  container:
    'background: white; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); overflow: hidden;',
  header: 'padding: 8px 12px; font-weight: 600; border-bottom: 1px solid #e5e7eb; background: #f9fafb;',
  row: 'padding: 6px 12px; display: flex; align-items: center; justify-content: space-between; gap: 12px;',
  labelContainer: 'display: flex; align-items: center; gap: 8px;',
  colorDot: 'width: 10px; height: 10px; border-radius: 2px;',
  label: 'color: #6B7280; font-size: 12px;',
  value: 'font-weight: 600; font-size: 12px;',
  footer: 'padding: 6px 12px; border-top: 1px solid #e5e7eb;'
} as const

export interface TooltipRowData {
  name: string
  value: string
  color: string
}

const buildTooltipRow = (row: TooltipRowData): string => {
  return `
    <div style="${tooltipStyles.row}">
      <div style="${tooltipStyles.labelContainer}">
        <span style="${tooltipStyles.colorDot} background-color: ${row.color};"></span>
        <span style="${tooltipStyles.label}">${row.name}:</span>
      </div>
      <span style="${tooltipStyles.value}">${row.value}</span>
    </div>
  `
}

export interface TooltipFooterData {
  label: string
  value: string
  color?: string
}

const buildTooltipFooter = (footer: TooltipFooterData): string => {
  const valueColor = footer.color || '#1F2937'
  return `
    <div style="${tooltipStyles.footer} ${tooltipStyles.row}">
      <span style="color: #1F2937; font-weight: 600;">${footer.label}:</span>
      <span style="font-weight: 600; color: ${valueColor};">${footer.value}</span>
    </div>
  `
}

export interface BuildTooltipOptions {
  headerLabel: string
  rows: TooltipRowData[]
  footer?: TooltipFooterData
}

export const buildTooltipHtml = (options: BuildTooltipOptions): string => {
  let content = `<div class="apexcharts-tooltip-custom" style="${tooltipStyles.container}">`
  content += `<div style="${tooltipStyles.header}">${options.headerLabel}</div>`

  options.rows.forEach((row) => {
    content += buildTooltipRow(row)
  })

  if (options.footer) {
    content += buildTooltipFooter(options.footer)
  }

  content += '</div>'
  return content
}

export interface StandardTooltipOptions {
  valueFormatter: (value: number) => string
  headerFormatter?: (label: string) => string
  /** Optional per-series formatter; when provided it takes precedence over valueFormatter */
  seriesValueFormatter?: (value: number, seriesIndex: number) => string
}

const resolveTooltipLabel = (dataPointIndex: number, w: TooltipContext['w']): string => {
  const label =
    w.globals.categoryLabels?.[dataPointIndex] ||
    w.globals.labels[dataPointIndex] ||
    w.globals.seriesX?.[0]?.[dataPointIndex] ||
    ''
  return String(label)
}

export const createStandardTooltip = (options: StandardTooltipOptions) => {
  const { valueFormatter, headerFormatter = (label) => label, seriesValueFormatter } = options
  const formatValue = (value: number, seriesIndex: number): string =>
    seriesValueFormatter ? seriesValueFormatter(value, seriesIndex) : valueFormatter(value)

  return ({ series, dataPointIndex, w }: TooltipContext): string => {
    const headerLabel = headerFormatter(resolveTooltipLabel(dataPointIndex, w))

    const rows: TooltipRowData[] = w.config.series
      .map((s, idx) => {
        const value = series[idx]?.[dataPointIndex]
        if (value === undefined || value === null) return null

        return {
          name: s.name || `Series ${idx + 1}`,
          value: formatValue(value, idx),
          color: s.color || w.globals.colors[idx] || '#6B7280'
        }
      })
      .filter((row): row is TooltipRowData => row !== null)

    return buildTooltipHtml({ headerLabel, rows })
  }
}

export const createTooltipWithFooter = (
  options: StandardTooltipOptions & {
    footerLabel: string
    footerValueGetter: (dataPointIndex: number) => number
    footerColorGetter?: (value: number) => string
  }
) => {
  const {
    valueFormatter,
    headerFormatter = (label) => label,
    footerLabel,
    footerValueGetter,
    footerColorGetter
  } = options

  return ({ series, dataPointIndex, w }: TooltipContext): string => {
    const headerLabel = headerFormatter(resolveTooltipLabel(dataPointIndex, w))

    const rows: TooltipRowData[] = w.config.series
      .map((s, idx) => {
        const value = series[idx]?.[dataPointIndex]
        if (value === undefined || value === null) return null

        return {
          name: s.name || `Series ${idx + 1}`,
          value: valueFormatter(value),
          color: s.color || w.globals.colors[idx] || '#6B7280'
        }
      })
      .filter((row): row is TooltipRowData => row !== null)

    const footerValue = footerValueGetter(dataPointIndex)
    const footer: TooltipFooterData = {
      label: footerLabel,
      value: valueFormatter(footerValue),
      color: footerColorGetter?.(footerValue)
    }

    return buildTooltipHtml({ headerLabel, rows, footer })
  }
}
